import './CustomFileTypeSelector.scss';
import React, {useEffect, useMemo, useState} from 'react';
import {Box, Typography, Menu, FormControlLabel, Checkbox} from '@mui/material';
import {useTranslation} from 'react-i18next';
import ArrFilterChip from '../../../../common/arr-filter-chip/ArrFilterChip';
import ArrButton from '../../../../common/arr-button/ArrButton';
import {FileTypeItem} from '../../../../../api/types/production.type';
import {ArrowDropDown} from '@mui/icons-material';

interface CustomFileTypeSelectorProps {
  onFileTypesChange: (fileTypes: FileTypeItem[]) => void;
  fileTypes?: FileTypeItem[];
  disabled?: boolean;
  checkboxDataTestId?: string;
}

const CustomFileTypeSelector: React.FC<CustomFileTypeSelectorProps> = ({
  onFileTypesChange,
  fileTypes = [],
  disabled = false,
  checkboxDataTestId = 'custom-file-type-selector',
}) => {
  const {t} = useTranslation();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selected, setSelected] = useState<string[]>([]);

  // derive available options
  const availableFileTypes = useMemo(
    () =>
      fileTypes.map((item) => ({
        label: item.fileTypeLabel,
        value: item.fileTypeID.toString(),
        isSelected: !!item.isFileTypeSelected,
      })),
    [fileTypes]
  );

  // sync local selection from props
  useEffect(() => {
    const preSelected = availableFileTypes
      .filter((f) => f.isSelected)
      .map((f) => f.value);
    setSelected(preSelected);
  }, [availableFileTypes]);

  const handleRemove = (fileTypeId: number) => {
    if (disabled) return;
    const updated = fileTypes.map((ft) =>
      ft.fileTypeID === fileTypeId ? {...ft, isFileTypeSelected: false} : ft
    );
    onFileTypesChange(updated);
    setSelected((prev) => prev.filter((id) => id !== String(fileTypeId)));
  };

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (disabled) return;
    setAnchorEl(event.currentTarget);
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleClose = (_event?: unknown, reason?: 'backdropClick') => {
    // if (reason === 'backdropClick') return;
    setAnchorEl(null);
  };

  const handleToggle = (value: string) => {
    if (disabled) return;
    setSelected((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]
    );
  };

  const handleSelectAll = () => {
    if (disabled) return;
    if (selected.length === availableFileTypes.length) {
      setSelected([]);
    } else {
      setSelected(availableFileTypes.map((f) => f.value));
    }
  };

  const handleClearAll = () => {
    if (disabled) return;
    setSelected([]);
  };

  const handleApply = () => {
    if (disabled) return;
    const idSet = new Set(selected);
    const updated = fileTypes.map((f) => ({
      ...f,
      isFileTypeSelected: idSet.has(f.fileTypeID.toString()),
    }));
    onFileTypesChange(updated);
    handleClose();
  };

  return (
    <>
      <Box className="custom-file-type-selector">
        <Box className="file-type-selector-header">
          <Typography
            className="typo-text subhead_size file-type-selector-label"
            data-testid={`custom-file-type-selector-label`}
          >
            {t(
              'productionsAndExports.createNewProduction.productionComponents.tiffPdfComponent.placeholders.fileTypes.label'
            )}
          </Typography>
          <ArrButton
            variant="primary"
            size="small"
            className="add-file-type-selector-btn typo-text buttonSmall_size"
            endIcon={<ArrowDropDown />}
            onClick={handleClick}
            disabled={disabled}
            dataTestId="custom-file-type-selector-select-file-types-button"
          >
            {t(
              'productionsAndExports.createNewProduction.productionComponents.tiffPdfComponent.placeholders.selectFileTypes'
            )}
          </ArrButton>
        </Box>
        <Box className="selected-file-type-selector-display">
          {fileTypes &&
            fileTypes.filter((ft) => ft.isFileTypeSelected).length > 0 &&
            fileTypes
              .filter((ft) => ft.isFileTypeSelected)
              .map((ft) => (
                <ArrFilterChip
                  key={ft.fileTypeID}
                  dataTestId={ft.fileTypeLabel
                    ?.replaceAll(' ', '')
                    .toLowerCase()}
                  classNames="arr-filter-chip-v2-radius"
                  filterName=""
                  selections={[
                    {
                      id: String(ft.fileTypeID),
                      value: ft.fileTypeLabel,
                    },
                  ]}
                  variant="v1"
                  isResponsive={false}
                  onRemove={
                    disabled ? undefined : () => handleRemove(ft.fileTypeID)
                  }
                  disabled={disabled}
                />
              ))}
        </Box>
      </Box>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={(event: object, reason: 'backdropClick' | 'escapeKeyDown') =>
          handleClose(
            event,
            reason === 'backdropClick' ? 'backdropClick' : undefined
          )
        }
        PaperProps={{
          className: 'custom-filetype-menu',
        }}
      >
        <div
          className="menu-content"
          data-testid={`custom-file-type-selector-select-all-wrapper`}
        >
          <FormControlLabel
            className="select-all"
             data-testid={`custom-file-type-selector-select-all-wrapper${checkboxDataTestId}`}
            control={
              <Checkbox
                data-testid={`${checkboxDataTestId}-select-all`}
                checked={selected.length === availableFileTypes.length}
                indeterminate={
                  selected.length > 0 &&
                  selected.length < availableFileTypes.length
                }
                onChange={handleSelectAll}
                disabled={disabled}
              />
            }
            label={t('common.button.selectAll')}
          />

          <div
            className="filetype-list"
            data-testid={`custom-file-type-selector-div`}
          >
            {availableFileTypes.map((type) => (
              <FormControlLabel
              data-testid={`custom-file-type-selector-div-${type.value}`}
                key={type.value}
                control={
                  <Checkbox
                    data-testid={`${checkboxDataTestId}-option-${type.value}`}
                    checked={selected.includes(type.value)}
                    onChange={() => handleToggle(type.value)}
                    disabled={disabled}
                  />
                }
                label={type.label}
              />
            ))}
          </div>

          <div
            className="menu-footer"
            data-testid={`custom-file-type-selector-clear-all-apply-btn`}
          >
            <ArrButton
              variant="text"
              size="small"
              onClick={handleClearAll}
              disabled={disabled}
              dataTestId="custom-file-type-selector-clear-all-button"
            >
              {t('common.button.clearAll')}
            </ArrButton>
            <ArrButton
              variant="primary"
              size="small"
              onClick={handleApply}
              disabled={disabled}
              dataTestId="custom-file-type-selector-apply-button"
            >
              {t('common.button.apply')}
            </ArrButton>
          </div>
        </div>
      </Menu>
    </>
  );
};

export default CustomFileTypeSelector;
